<template>
  <div ref="stepTop">
    <component v-for="(item, index) in currentSections" :key="index" :is="resolveSection(item).component"
      v-bind="resolveSection(item).props" :ref="(el) => {
        // console.log('el', el);
        if (el) {
          sectionRefs[item.name] = el;
        }
      }" :sectionRefs="sectionRefs" class="section" />
    <NavigationButtons :scrollToFirstInvalidSection="scrollToFirstInvalidSection" />

  </div>
</template>

<script>
import { computed, watch, ref, defineAsyncComponent, reactive } from "vue";
import { useStore } from "vuex";
import NavigationButtons from "@/components/form/NavigationButtons.vue";


/**
 * The `FormStep` component is responsible for rendering the sections of the current step in a form.
 *
 * It uses the `useStore` hook to access the Vuex store and retrieve the current form configuration, step, and submission state.
 *
 * The component dynamically loads and renders the appropriate section components based on the current form configuration.
 * It also provides functionality to scroll to the top of the current step or a specific section within the step.
 *
 * The `resolveSection` function is used to map the section objects from the form configuration to their corresponding Vue components and props.
 *
 * The `scrollToTop` and `scrollToSection` functions are used to scroll the page to the top of the current step or a specific section, respectively.
 *
 * The `scrollToFirstInvalidSection` function is used to scroll the page to the first invalid section in the current step.
 */
export default {
  name: "FormStep",
  components: {
    NavigationButtons
  },
  setup() {
    const store = useStore();

    /**
     * A ref to the top of the current step in the form.
     * This is used to scroll to the top of the step when the current step changes.
     */
    const stepTop = ref(null);

    /**
     * A reactive object that stores references to the section components rendered in the `FormStep` component.
     * The keys of the object are the names of the sections, and the values are references to the corresponding section components.
     * This object is used to provide access to the section components from the parent component, for example to scroll to a specific section.
     */
    const sectionRefs = reactive({});

    /**
     * Computes the current form configuration from the Vuex store.
     * This computed property retrieves the current form configuration from the Vuex store
     * using the `getCurrentFormConfig` getter.
     *
     * @returns {Object} The current form configuration.
     */
    const currentFormConfig = computed(
      () => store.getters["config/getCurrentFormConfig"]
    );

    /**
     * Computed properties that retrieve the current step and submission state of the form from the Vuex store.
     *
     * `currentStep` is a computed property that returns the current step of the form, as stored in the Vuex store's `validation` state.
     *
     * `isSubmitted` is a computed property that returns a boolean indicating whether the form has been submitted, as stored in the Vuex store's `validation` state.
     *
     * These computed properties are used throughout the `FormStep` component to determine the current state of the form and render the appropriate content.
     */
    const currentStep = computed(() => store.state.validation.currentStep);
    const isSubmitted = computed(() => store.state.validation.isSubmitted);

    /**
     * Computes the unique sections across all steps and the "Thanks" page in the current form configuration.
     * This is used to dynamically load the appropriate section components.
     *
     * @returns {string[]} An array of unique section names.
     */
    const uniqueSections = computed(() => {
      const allSections = [
        ...currentFormConfig.value.steps.flatMap((step) => step.sections),
        ...currentFormConfig.value.thanksPage.sections,
      ];
      return [...new Set(allSections.map((section) => section.name))];
    });

    /**
     * Computes a mapping of section names to their corresponding Vue components.
     * This is used to dynamically load and render the appropriate section components
     * based on the current form configuration.
     *
     * The mapping is created by iterating over the unique section names and
     * dynamically importing the corresponding Vue component for each section.
     *
     * @returns {Object} A mapping of section names to their corresponding Vue components.
     */
    const sectionMap = computed(() =>
      Object.fromEntries(
        uniqueSections.value.map((sectionName) => [
          sectionName,
          defineAsyncComponent(() =>
            import(`@/components/sections/${sectionName}.vue`)
          ),
        ])
      )
    );

    /**
     * Computes the sections to be displayed for the current step of the form.
     * If the form has been submitted, it returns the sections for the "Thanks" page.
     * Otherwise, it returns the sections for the current step of the form.
     * If the form configuration or the current step is invalid, it returns an empty array.
     *
     * @returns {Object[]} An array of section objects, each containing a component and props.
     */
    const currentSections = computed(() => {
      if (isSubmitted.value) {
        return currentFormConfig.value.thanksPage.sections;
      }
      if (
        !currentFormConfig.value ||
        !currentFormConfig.value.steps ||
        currentFormConfig.value.steps.length < currentStep.value
      ) {
        console.error("Invalid form configuration or step");
        return [];
      }
      return currentFormConfig.value.steps[currentStep.value - 1].sections;
    });

    /**
     * Resolves the Vue component and props for a given section in the form.
     *
     * @param {Object} item - The section object containing the name and parameters.
     * @param {string} item.name - The name of the section.
     * @param {Object} item.parameters - The parameters for the section.
     * @returns {Object|null} - An object containing the resolved Vue component and props, or null if the section is unknown.
     */
    const resolveSection = (item) => {
      const component = sectionMap.value[item.name];
      if (component) {

        return { component, props: item.parameters };
      }
      console.warn(`Unknown section: ${item.name}`);
      return null;
    };

    /**
     * Scrolls the page to the top of the current step in the form.
     *
     * This function is called whenever the current step in the form changes. It
     * scrolls the page to the top of the current step, providing a smooth
     * scrolling animation.
     */
    const scrollToTop = () => {
      if (stepTop.value) {
        const yOffset = -20; // 20px above the step
        const y =
          stepTop.value.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    };

    /**
     * Scrolls the page to the top of the specified section in the form.
     *
     * @param {string} sectionName - The name of the section to scroll to.
     */
    const scrollToSection = (sectionName) => {
      console.log('Scoll To Section: ', sectionName);
      return;



      // if (sectionRefs[sectionName]) {
      //   const sectionElement = sectionRefs[sectionName].$el;
      //   const yOffset = -20; // 20px above the section
      //   const y =
      //     sectionElement.getBoundingClientRect().top + window.scrollY + yOffset;
      //   window.scrollTo({ top: y, behavior: "smooth" });
      // }
    };

    /**
     * Scrolls the page to the first invalid section in the current form step.
     *
     * This function is called to automatically scroll the page to the first section
     * in the current form step that has an invalid validation state. This helps
     * the user quickly identify and fix any invalid form fields.
     */
    const scrollToFirstInvalidSection = () => {
      const firstInvalidSection = store.getters["validation/getFirstInvalidSection"](
        currentStep.value
      );
      
      console.log('Attempting to scroll to section:', firstInvalidSection);
      
      if (firstInvalidSection && sectionRefs[firstInvalidSection]) {
        const sectionElement = sectionRefs[firstInvalidSection];
        console.log('Section element:', sectionElement);
        
        // Check if sectionElement is a Vue component instance
        const elementToScroll = sectionElement.$el || sectionElement;

        // Ensure the element is a DOM element
        if (elementToScroll instanceof HTMLElement) {
          const yOffset = -20;
          window.scrollTo({
            top: elementToScroll.getBoundingClientRect().top + window.pageYOffset + yOffset,
            behavior: 'smooth'
          });
        } else {
          console.log('No valid element found to scroll');
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    };

    /**
     * Watches the `currentStep` reactive property and calls the `scrollToTop` function whenever it changes.
     *
     * This ensures that the page scrolls to the top of the current step in the form whenever the user navigates to a new step.
     */
    watch(currentStep, () => {
      console.log('Current step changed:', currentStep.value);
      scrollToTop();
    });

    return {
      currentSections,
      resolveSection,
      stepTop,
      sectionRefs,
      scrollToSection,
      scrollToFirstInvalidSection,
    };
  },
};
</script>

<style scoped>
.section {
  margin-top: 20px;
}

.form_wrap {
  margin-bottom: 0;
}
</style>
